import {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import useBodyClass from '../../../common/hooks/useBodyClassName';
import Switch from '../../../common/Switch';
import Button from '../../../common/Button';
import {constants} from '../../../common/constants/constants';
import {hideLoader, showLoader} from '../../../store/common/slice';
import medal from '../../../images/newDesign/medal.svg';
import emoji from '../../../images/newDesign/emoji.svg';
import arrowInCircle from '../../../images/newDesign/arrow_in_white_circle.svg';
import PaymentFlowContainer from '../PaymentFlowContainer';
import {StyledPaymentPage} from './PaymentPage.styled';
import {MonthlyCard} from './MonthlyCard';
import {SpecialPaymentCard} from './SpecialPaymentCard';
import {
    fetchApartmentPaymentData,
    fetchTenantStatus,
    paymentsCardType,
} from './PaymentPageLogic';
import {
    setAllPayments,
    setDebtSelectedPayments,
    setOneTimeSelectedPayments,
    setOnGoingSelectedPayments
} from "../../../store/tenant/slice";
import {getAuthenticatedUser, verifyLoggedIn} from '../../../store/auth/actions';
import { TenantRecordConfirmModal } from './TenantRecordConfirmModal';
import {NotificationManager} from "react-notifications";

export function PaymentPage({ match }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const { buildingID, apartmentNumber } = match.params;

    const accessToken = useSelector((state) => state.auth.accessToken);
    const ltr = useSelector(state => state.common.ltrEnabled);
    const tenantDetails = useSelector(state => state.tenant.details);
    const payments = useSelector(state => state.tenant.allPayments);
    const paymentsCount = useSelector(state => state.tenant.allPaymentsCount);
    const onGoingSelectedPayments = useSelector(state => state.tenant.onGoingSelectedPayments);
    const oneTimeSelectedPayments = useSelector(state => state.tenant.oneTimeSelectedPayments);
    const debtSelectedPayments = useSelector(state => state.tenant.debtSelectedPayments);
    const paymentMethod = useSelector(state => state.tenant.paymentMethod);
    const currentBuildingData = useSelector(state => state.buildings.currentBuildingData);
    const externalPartnerId = currentBuildingData?.managementCompany?.externalId;
    const isRenter = tenantDetails?.tenant_type === 'renter';
    const isOwner = tenantDetails?.tenant_type === 'owner';

    const [hasOwner, setHasOwner] = useState(false);
    const [hasRenter, setHasRenter] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [switchChecked, setSwitchChecked] = useState(false);
    const [tenantRecordConfirmType, setTenantRecordConfirmType] = useState(null);

    const firstRender = useRef(true);

    const allowedInternalBuildingIds = ['1904'];
    const allowedExternalPartnerIds = ['bllink', 'agas'];
    const isPrivatePartner = externalPartnerId === null;

    const isEntityAllowed = (
        allowedInternalBuildingIds.includes(buildingID) ||
        allowedExternalPartnerIds.includes(externalPartnerId) ||
        isPrivatePartner);

    // not relevant for this check should only consider isRenter/isOwner
    const isManager = !!accessToken;
    const hasBothTenants = hasOwner && hasRenter;
    const hasOneTimePayments = payments?.one_time_payments?.length > 0;
    const hasOnGoingPayments = payments?.on_going_payments?.paymentsSortedArray?.length > 0;
    const hasPastPayments = payments?.past_payments?.paymentsSortedArray?.length > 0;
    const onlyOneCardToShow = !hasOneTimePayments || (!hasOnGoingPayments && hasPastPayments);
    const mustShowSwitcher = hasBothTenants && !onlyOneCardToShow;
    const mustShowManagerInfo = hasOneTimePayments && (isOwner || switchChecked || !hasBothTenants);
    const mustShowRenterInfo = (hasOnGoingPayments || hasPastPayments) && (isRenter || switchChecked || !hasBothTenants);
    const switcherLabel = isOwner ? t('payments.show_renter_payments') : t('payments.show_owner_payments');

    // default switch value
    useEffect(() => {
        if (onlyOneCardToShow) {
            setSwitchChecked(true);
        } else if (mustShowSwitcher && firstRender.current) {
            firstRender.current = false;
            setSwitchChecked(false);
        } else {
            setSwitchChecked(false);
        }
    }, [isRenter, isOwner, isManager, mustShowSwitcher]);

    useBodyClass('payment-flow');

    useEffect(async () => {
        if (tenantDetails == null) {
            history.goBack();
        }

        dispatch(showLoader());
        const {payload} = await dispatch(getAuthenticatedUser());
        if (payload?.accessToken) {
            await dispatch(verifyLoggedIn());
        }
        const ownership = isManager ? null : tenantDetails?.tenant_type;
        const phone = tenantDetails?.phone;
        try {
            if (payments == null) {
                const result = await fetchApartmentPaymentData(buildingID, apartmentNumber, accessToken, ownership, phone);
                if (result == null) {
                    // NOTE: it will de undefined due to client error checking.
                    // WARNING: some buildings have phone numbers checking feature enabled
                    history.goBack();
                    return;
                }
                dispatch(setAllPayments(result));
            }
            const tenantStatus = await fetchTenantStatus(buildingID, apartmentNumber);
            if (tenantStatus) {
                setHasRenter(tenantStatus.hasRenter);
                setHasOwner(tenantStatus.hasOwner);
            }
            setDataLoaded(true);
        } catch (e) {
            console.error(e);
            history.goBack();
        } finally {
            dispatch(hideLoader());
        }
    }, [tenantDetails]);

    const subheader = useMemo(
        () => paymentsCount > 0 ? t('payments.payment_will_renew') : (dataLoaded ? (<>{t('payments.no_open_payments')} <img className='emoji' src={emoji} /></>) : ''),
        [paymentsCount, dataLoaded]
    );

    function handlePageBack() {
        dispatch(setAllPayments(null));
    }

    function handleContinueBtnClick() {
        // commented out because bug of ticket https://bllink.atlassian.net/jira/software/projects/SSB/boards/7?assignee=63df7122a5d0c826306ba6a5&selectedIssue=SSB-974
        // if (!isManager && !switchChecked) {
        //     dispatch(setOneTimeSelectedPayments([]));
        // }

        if (isManager && !switchChecked) {
            dispatch(setOnGoingSelectedPayments([]));
            dispatch(setDebtSelectedPayments([]));
        }

        if (!switchChecked) {
            if (isRenter) {
                dispatch(setOneTimeSelectedPayments([]));
            } else {
                dispatch(setOnGoingSelectedPayments([]));
                dispatch(setDebtSelectedPayments([]));
            }
        }

        const recurringPaymentsLength = onGoingSelectedPayments.length + debtSelectedPayments.length;
        const oneTimePaymentsLength = oneTimeSelectedPayments.length;
        const allSelectedLength = recurringPaymentsLength + oneTimePaymentsLength
        if (!mustShowManagerInfo && recurringPaymentsLength === 0 || mustShowManagerInfo && allSelectedLength === 0) {
            NotificationManager.warning(t('payments.cart_empty_text'));
            return;
        }
        if (isEntityAllowed && paymentMethod === constants.APIContract.paymentMethodsKeys.bankCharge) {
            history.push(`/${constants.pages.chooseBankMethod}/${buildingID}/${apartmentNumber}`)
        } else {
            history.push({pathname: `/${constants.pages.lastConfirmPaymentPageUrl}-new/${buildingID}/${apartmentNumber}`});
        }
    }

    async function handleMarkAsPaidClose(marked) {
        setTenantRecordConfirmType(null);
        if (marked) {
            try {
                const ownership = isManager ? null : tenantDetails?.tenant_type;
                const phone = tenantDetails.phone;
                dispatch(showLoader());
                const result = await fetchApartmentPaymentData(buildingID, apartmentNumber, accessToken, ownership, phone);
                if (result == null) {
                    // NOTE: it will de undefined due to client error checking.
                    // WARNING: some buildings have phone numbers checking feature enabled
                    history.goBack();
                    return;
                }
                dispatch(setAllPayments(result));
            } catch (e) {
                console.error(e);
            } finally {
                dispatch(hideLoader());
            }
        }
    }

    return (
        <PaymentFlowContainer title={t('payments.payment_for')} subheader={subheader} showHeaderMessage onPageBack={handlePageBack}>

            {paymentsCount > 0 ? (
                <StyledPaymentPage ltr={ltr}>
                    {mustShowSwitcher ? (
                        <Switch
                            checked={switchChecked}
                            onChange={() => setSwitchChecked(!switchChecked)}
                            label={switcherLabel}
                            className='switch'
                        />
                    ) : null}
                    <div className='cards-wrapper'>
                        {mustShowRenterInfo && <MonthlyCard onMarkAsPaid={() => setTenantRecordConfirmType(paymentsCardType.monthly)} /> }
                        {mustShowManagerInfo && <SpecialPaymentCard onMarkAsPaid={() => setTenantRecordConfirmType(paymentsCardType.special)} /> }
                    </div>

                    <div className='btn-wrapper'>
                        <Button onClick={handleContinueBtnClick}>
                            {t('expenses.set_expenses.continue')} <img className='arrow' src={arrowInCircle} />
                        </Button>
                    </div>
                </StyledPaymentPage>) : (
                <StyledPaymentPage>
                    { dataLoaded && <img className='medal' src={medal} /> }
                </StyledPaymentPage>
            )}
            { tenantRecordConfirmType != null && (
                <TenantRecordConfirmModal
                    buildingID={buildingID}
                    apartmentNumber={apartmentNumber}
                    type={tenantRecordConfirmType}
                    onClose={(marked) => handleMarkAsPaidClose(marked)}
                />
            ) }
        </PaymentFlowContainer>
    )
}
